/**
 * Variant Form Drawer Component
 * Create/Edit Variant form in a drawer
 */

import { useEffect, useMemo, useState } from 'react';
import {
  useTranslation,
  useForm,
  zodResolver,
  z,
  Controller,
} from '@ufc/core';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  BaseDrawer,
  Button,
  Input,
  Label,
  Textarea,
  Switch,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  ToggleGroup,
  ToggleGroupItem,
  cn,
} from '@ufc/uikit';
import { Box, ImageIcon, Save, Trash2, Upload, X } from 'lucide-react';
import { mediaApi } from '../../infrastructure/api';
import type {
  Variant,
  SKUSummary,
  CreateVariantRequest,
  UpdateVariantRequest,
  MediaAsset,
} from '../../domain/types';
import { getLocalizedName } from '../../domain/utils';
import {
  PACKAGING_TYPES,
  PACKAGING_CONFIG,
  PORTAL_AVAILABILITY_OPTIONS,
  PORTAL_AVAILABILITY_CONFIG,
  DELIVERY_GROUPING_RULES,
  DELIVERY_GROUPING_CONFIG,
} from '../../domain/constants';

// ============================================================================
// Form Schema
// ============================================================================

// Form data interface (manually defined for better type compatibility)
interface VariantFormData {
  skuDocumentId: string;
  name: { en: string; ar?: string };
  description?: { en?: string; ar?: string };
  variantCode: string;
  barcode: string;
  weight: number;
  packaging: 'bags' | 'bulk';
  portalAvailability: 'ecommerce' | 'portal' | 'both';
  deliveryGroupingRule: 'allow_mixed' | 'require_same';
  isActive: boolean;
  sapMaterialCode?: string;
  sapStorageLocationCode?: string;
  minQuantity?: number | null;
  maxQuantity?: number | null;
  minOrderQuantity?: number | null;
  maxOrderQuantity?: number | null;
}

const variantFormSchema = z.object({
  skuDocumentId: z.string().min(1, 'SKU is required'),
  name: z.object({
    en: z.string().min(1, 'Name (English) is required'),
    ar: z.string().optional(),
  }),
  description: z
    .object({
      en: z.string().optional(),
      ar: z.string().optional(),
    })
    .optional(),
  variantCode: z.string().min(1, 'Variant code is required'),
  barcode: z.string().min(1, 'Barcode is required'),
  weight: z.number().min(1, 'Weight must be at least 1'),
  packaging: z.enum(['bags', 'bulk']),
  portalAvailability: z.enum(['ecommerce', 'portal', 'both']),
  deliveryGroupingRule: z.enum(['allow_mixed', 'require_same']),
  isActive: z.boolean(),
  sapMaterialCode: z.string().max(50).optional(),
  sapStorageLocationCode: z.string().max(10).optional(),
  minQuantity: z.number().min(1, 'Minimum quantity must be at least 1').nullable().optional(),
  maxQuantity: z.number().positive('Maximum quantity must be positive').nullable().optional(),
  minOrderQuantity: z.number().min(1, 'Minimum order quantity must be at least 1').nullable().optional(),
  maxOrderQuantity: z.number().positive('Maximum order quantity must be positive').nullable().optional(),
});

// ============================================================================
// Helper Functions
// ============================================================================

function variantToFormData(
  variant: Variant | null,
  defaultSkuId?: string,
): VariantFormData {
  if (!variant) {
    return {
      skuDocumentId: defaultSkuId || '',
      name: { en: '', ar: '' },
      description: { en: '', ar: '' },
      variantCode: '',
      barcode: '',
      weight: 25,
      packaging: 'bulk',
      portalAvailability: 'both',
      deliveryGroupingRule: 'require_same',
      isActive: true,
      sapMaterialCode: '',
      sapStorageLocationCode: '',
      minQuantity: 1,
      maxQuantity: null,
      minOrderQuantity: null,
      maxOrderQuantity: null,
    };
  }

  return {
    skuDocumentId: variant.skuDocumentId || variant.sku?.documentId || '',
    name: {
      en: variant.name.en,
      ar: variant.name.ar || '',
    },
    description: {
      en: variant.description?.en || '',
      ar: variant.description?.ar || '',
    },
    variantCode: variant.variantCode,
    barcode: variant.barcode,
    weight: variant.weight,
    packaging: variant.packaging,
    portalAvailability: variant.portalAvailability ?? 'both',
    deliveryGroupingRule: variant.deliveryGroupingRule,
    isActive: variant.isActive,
    sapMaterialCode: variant.sapMaterialCode || '',
    sapStorageLocationCode: variant.sapStorageLocationCode || '',
    minQuantity: variant.minQuantity ?? 1,
    maxQuantity: variant.maxQuantity ?? null,
    minOrderQuantity: variant.minOrderQuantity ?? null,
    maxOrderQuantity: variant.maxOrderQuantity ?? null,
  };
}

function formDataToCreateRequest(data: VariantFormData): CreateVariantRequest {
  return {
    skuDocumentId: data.skuDocumentId,
    name: {
      en: data.name.en,
      ar: data.name.ar || undefined,
    },
    description:
      data.description?.en || data.description?.ar
        ? {
            en: data.description?.en || '',
            ar: data.description?.ar || undefined,
          }
        : undefined,
    variantCode: data.variantCode,
    barcode: data.barcode,
    weight: data.weight,
    packaging: data.packaging,
    portalAvailability: data.portalAvailability,
    deliveryGroupingRule: data.deliveryGroupingRule,
    isActive: data.isActive,
    sapMaterialCode: data.sapMaterialCode || undefined,
    sapStorageLocationCode: data.sapStorageLocationCode || undefined,
    minQuantity: data.minQuantity ?? null,
    maxQuantity: data.maxQuantity ?? null,
    minOrderQuantity: data.minOrderQuantity ?? null,
    maxOrderQuantity: data.maxOrderQuantity ?? null,
  };
}

function formDataToCreateRequestWithImages(
  data: VariantFormData,
  imageIds?: string[],
): CreateVariantRequest {
  return {
    ...formDataToCreateRequest(data),
    imageIds,
  };
}

function formDataToUpdateRequest(
  data: VariantFormData,
  documentId: string,
  imageIds?: string[],
): UpdateVariantRequest {
  return {
    ...formDataToCreateRequestWithImages(data, imageIds),
    documentId,
  };
}

function isPortalAvailabilityOption(
  value: string,
): value is 'ecommerce' | 'portal' | 'both' {
  return value === 'ecommerce' || value === 'portal' || value === 'both';
}

type EditableImage = {
  id?: string;
  url: string;
};

function variantToEditableImages(variant: Variant | null): EditableImage[] {
  if (!variant) return [];

  if (variant.imageMedia && variant.imageMedia.length > 0) {
    return variant.imageMedia.map((image) => ({
      id: image.id,
      url: image.url,
    }));
  }

  return (variant.images ?? []).map((url) => ({ url }));
}

function getImageNameFromUrl(url: string): string {
  const cleaned = url.split('?')[0];
  const parts = cleaned.split('/');
  return parts[parts.length - 1] || url;
}

function mergeImageIds(
  images: EditableImage[],
  uploaded: MediaAsset[],
): string[] {
  const existing = images
    .map((image) => image.id)
    .filter((id): id is string => Boolean(id));
  const added = uploaded.map((image) => image.id);
  return [...existing, ...added];
}

type NewImagePreview = {
  name: string;
  url: string;
};

function buildNewImagePreviews(files: File[]): NewImagePreview[] {
  return files.map((file) => ({
    name: file.name,
    url: URL.createObjectURL(file),
  }));
}

function cleanupNewImagePreviews(previews: NewImagePreview[]): void {
  previews.forEach((preview) => {
    URL.revokeObjectURL(preview.url);
  });
}

// ============================================================================
// Component Props
// ============================================================================

interface VariantFormDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  variant: Variant | null;
  skus: SKUSummary[];
  defaultSkuId?: string;
  onSubmit: (data: CreateVariantRequest | UpdateVariantRequest) => void;
  isSaving?: boolean;
  locale?: 'en' | 'ar';
  pickImageFiles?: () => Promise<File[]>;
}

// ============================================================================
// Component
// ============================================================================

export function VariantFormDrawer({
  isOpen,
  onClose,
  variant,
  skus,
  defaultSkuId,
  onSubmit,
  isSaving = false,
  locale = 'en',
  pickImageFiles,
}: VariantFormDrawerProps) {
  const { t } = useTranslation();
  const isEditing = variant !== null;
  const [existingImages, setExistingImages] = useState<EditableImage[]>([]);
  const [newImageFiles, setNewImageFiles] = useState<File[]>([]);
  const [hasImageChanges, setHasImageChanges] = useState(false);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const isMutating = isSaving || isUploadingImages;
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:1337';

  const defaultValues = useMemo(
    () => variantToFormData(variant, defaultSkuId),
    [variant, defaultSkuId],
  );

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<VariantFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(variantFormSchema) as any,
    defaultValues,
  });

  // Reset form when drawer opens/closes or variant changes
  useEffect(() => {
    if (isOpen) {
      reset(variantToFormData(variant, defaultSkuId));
      setExistingImages(variantToEditableImages(variant));
      setNewImageFiles([]);
      setHasImageChanges(false);
      setImageError(null);
    }
  }, [isOpen, variant, defaultSkuId, reset]);

  const newImagePreviews = useMemo(
    () => buildNewImagePreviews(newImageFiles),
    [newImageFiles],
  );

  useEffect(() => {
    return () => {
      cleanupNewImagePreviews(newImagePreviews);
    };
  }, [newImagePreviews]);

  const isActive = watch('isActive');

  const handleFormSubmit = async (data: VariantFormData) => {
    setImageError(null);
    setIsUploadingImages(true);

    try {
      const uploadedImages =
        newImageFiles.length > 0
          ? await mediaApi.uploadImages(newImageFiles)
          : [];

      const shouldSendImageIds =
        hasImageChanges || uploadedImages.length > 0 || !isEditing;
      const hasImagesWithoutIds = existingImages.some((image) => !image.id);

      if (shouldSendImageIds && hasImagesWithoutIds) {
        setImageError(
          t(
            'products.form.imagesRefreshHint',
            'Please refresh and retry editing images.',
          ),
        );
        setIsUploadingImages(false);
        return;
      }

      const imageIds = shouldSendImageIds
        ? mergeImageIds(existingImages, uploadedImages)
        : undefined;

      if (isEditing && variant) {
        await Promise.resolve(
          onSubmit(formDataToUpdateRequest(data, variant.documentId, imageIds)),
        );
      } else {
        await Promise.resolve(
          onSubmit(formDataToCreateRequestWithImages(data, imageIds)),
        );
      }
    } finally {
      setIsUploadingImages(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const handleImageFilesChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = Array.from(event.target.files ?? []);
    if (!files.length) return;
    setNewImageFiles((prev) => [...prev, ...files]);
    setHasImageChanges(true);
    setImageError(null);
    event.currentTarget.value = '';
  };

  const handlePickImageFiles = async () => {
    if (!pickImageFiles) return;

    const files = await pickImageFiles();
    if (!files.length) return;

    setNewImageFiles((prev) => [...prev, ...files]);
    setHasImageChanges(true);
    setImageError(null);
  };

  const handleRemoveExistingImage = (index: number) => {
    setExistingImages((prev) =>
      prev.filter((_, imageIndex) => imageIndex !== index),
    );
    setHasImageChanges(true);
    setImageError(null);
  };

  const handleRemoveNewFile = (index: number) => {
    setNewImageFiles((prev) =>
      prev.filter((_, fileIndex) => fileIndex !== index),
    );
    setImageError(null);
  };

  const headerContent = (
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/20">
        <Box className="h-5 w-5 text-white" />
      </div>
      <div>
        <h2 className="text-lg font-semibold text-white">
          {isEditing ? t('products.editVariant') : t('products.addVariant')}
        </h2>
        <p className="text-sm text-white/70">
          {isEditing ? variant?.variantCode : t('products.subtitle')}
        </p>
      </div>
    </div>
  );

  return (
    <BaseDrawer
      open={isOpen}
      onOpenChange={handleOpenChange}
      headerGradient="from-primary to-primary/80"
      headerContent={headerContent}
    >
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <form
        onSubmit={handleSubmit(handleFormSubmit as any)}
        className="flex flex-col gap-6 p-6"
      >
        {/* SKU Selection */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="sku">
            {t('products.form.sku')} <span className="text-destructive">*</span>
          </Label>
          <Controller
            name="skuDocumentId"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled={isMutating || isEditing}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('products.form.selectSku')} />
                </SelectTrigger>
                <SelectContent>
                  {skus.map((sku) => (
                    <SelectItem key={sku.documentId} value={sku.documentId}>
                      {getLocalizedName(sku.name, locale)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors.skuDocumentId && (
            <p className="text-sm text-destructive">
              {errors.skuDocumentId.message}
            </p>
          )}
        </div>

        {/* Name (English) */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="name-en">
            {t('products.form.nameEn')}{' '}
            <span className="text-destructive">*</span>
          </Label>
          <Input
            id="name-en"
            {...register('name.en')}
            placeholder={t('products.form.nameEn')}
            disabled={isMutating}
          />
          {errors.name?.en && (
            <p className="text-sm text-destructive">{errors.name.en.message}</p>
          )}
        </div>

        {/* Name (Arabic) */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="name-ar">{t('products.form.nameAr')}</Label>
          <Input
            id="name-ar"
            {...register('name.ar')}
            placeholder={t('products.form.nameAr')}
            disabled={isMutating}
            dir="rtl"
          />
        </div>

        {/* Variant Code & Barcode */}
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="variant-code">
              {t('products.form.variantCode')}{' '}
              <span className="text-destructive">*</span>
            </Label>
            <Input
              id="variant-code"
              {...register('variantCode')}
              placeholder={t('products.form.variantCodePlaceholder')}
              disabled={isMutating}
              className="font-mono"
            />
            {errors.variantCode && (
              <p className="text-sm text-destructive">
                {errors.variantCode.message}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="barcode">
              {t('products.form.barcode')}{' '}
              <span className="text-destructive">*</span>
            </Label>
            <Input
              id="barcode"
              {...register('barcode')}
              placeholder="1234567890"
              disabled={isMutating}
              className="font-mono"
            />
            {errors.barcode && (
              <p className="text-sm text-destructive">
                {errors.barcode.message}
              </p>
            )}
          </div>
        </div>

        {/* Weight */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="weight">
            {t('products.form.weight')}{' '}
            <span className="text-destructive">*</span>
          </Label>
          <Input
            id="weight"
            type="number"
            {...register('weight', { valueAsNumber: true })}
            placeholder="25"
            disabled={isMutating}
            min={1}
          />
          {errors.weight && (
            <p className="text-sm text-destructive">{errors.weight.message}</p>
          )}
        </div>

        {/* Sales Order Quantity Limits */}
        <div className="flex flex-col gap-3 rounded-lg border border-border/70 bg-muted/10 p-3">
          <p className="text-sm font-medium text-foreground">
            {t('products.form.salesOrderQuantityLimits', 'Sales Order Quantity Limits')}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-2">
              <Label htmlFor="min-quantity">
                {t('products.form.minQuantity', 'Min Quantity')}
              </Label>
              <Input
                id="min-quantity"
                type="number"
                {...register('minQuantity', { valueAsNumber: true, setValueAs: (v) => (v === '' || isNaN(v) ? null : Number(v)) })}
                placeholder="1"
                disabled={isMutating}
                min={1}
                step={1}
              />
              {errors.minQuantity && (
                <p className="text-xs text-destructive">{errors.minQuantity.message}</p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="max-quantity">
                {t('products.form.maxQuantity', 'Max Quantity')}{' '}
                <span className="text-muted-foreground text-xs">({t('products.form.optional', 'optional')})</span>
              </Label>
              <Input
                id="max-quantity"
                type="number"
                {...register('maxQuantity', { setValueAs: (v) => (v === '' || v === null || isNaN(Number(v)) ? null : Number(v)) })}
                placeholder={t('products.form.unlimited', 'Unlimited')}
                disabled={isMutating}
                min={1}
                step={1}
              />
              {errors.maxQuantity && (
                <p className="text-xs text-destructive">{errors.maxQuantity.message}</p>
              )}
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            {t('products.form.salesOrderQuantityLimitsHint', 'Set the minimum sales order quantity (default: 1) and maximum (leave empty for unlimited).')}
          </p>
        </div>

        {/* Order Quantity Limits */}
        <div className="flex flex-col gap-3 rounded-lg border border-border/70 bg-muted/10 p-3">
          <p className="text-sm font-medium text-foreground">
            {t('products.form.orderQuantityLimits', 'Order Quantity Limits')}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-2">
              <Label htmlFor="min-order-quantity">
                {t('products.form.minOrderQuantity', 'Min Order Quantity')}
              </Label>
              <Input
                id="min-order-quantity"
                type="number"
                {...register('minOrderQuantity', { valueAsNumber: true, setValueAs: (v) => (v === '' || isNaN(v) ? null : Number(v)) })}
                placeholder={t('products.form.noMinimum', 'No minimum')}
                disabled={isMutating}
                min={1}
                step={1}
              />
              {errors.minOrderQuantity && (
                <p className="text-xs text-destructive">{errors.minOrderQuantity.message}</p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="max-order-quantity">
                {t('products.form.maxOrderQuantity', 'Max Order Quantity')}{' '}
                <span className="text-muted-foreground text-xs">({t('products.form.optional', 'optional')})</span>
              </Label>
              <Input
                id="max-order-quantity"
                type="number"
                {...register('maxOrderQuantity', { setValueAs: (v) => (v === '' || v === null || isNaN(Number(v)) ? null : Number(v)) })}
                placeholder={t('products.form.unlimited', 'Unlimited')}
                disabled={isMutating}
                min={1}
                step={1}
              />
              {errors.maxOrderQuantity && (
                <p className="text-xs text-destructive">{errors.maxOrderQuantity.message}</p>
              )}
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            {t('products.form.orderQuantityLimitsHint', 'Generated fulfillment orders outside this range will be dropped after split by is applied.')}
          </p>
        </div>

        {/* Packaging Type */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="packaging">
            {t('products.form.packaging')}{' '}
            <span className="text-destructive">*</span>
          </Label>
          <Controller
            name="packaging"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled={isMutating}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('products.form.packaging')} />
                </SelectTrigger>
                <SelectContent>
                  {PACKAGING_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(PACKAGING_CONFIG[type].labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        {/* Portal Availability */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="portal-availability">
            {t('products.form.portalAvailability')}
          </Label>
          <Controller
            name="portalAvailability"
            control={control}
            render={({ field }) => (
              <div
                id="portal-availability"
                className="rounded-lg border border-border/70 bg-muted/20 p-2"
              >
                <ToggleGroup
                  type="single"
                  value={field.value}
                  onValueChange={(value) => {
                    if (isPortalAvailabilityOption(value))
                      field.onChange(value);
                  }}
                  disabled={isMutating}
                  variant="outline"
                  size="sm"
                  className={cn(
                    'w-full rounded-xl border-border/70 bg-background p-1 shadow-inner',
                    isMutating && 'opacity-80',
                  )}
                >
                  {PORTAL_AVAILABILITY_OPTIONS.map((option) => (
                    <ToggleGroupItem
                      key={option}
                      value={option}
                      className={cn(
                        'h-8 flex-1 rounded-lg px-2 text-[12px] font-semibold capitalize',
                        'text-muted-foreground transition-colors',
                        'data-[state=on]:border-primary/30 data-[state=on]:bg-primary',
                        'data-[state=on]:text-primary-foreground data-[state=on]:shadow-sm',
                      )}
                    >
                      {t(PORTAL_AVAILABILITY_CONFIG[option].labelKey)}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </div>
            )}
          />
        </div>

        {/* Delivery Grouping Rule */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="delivery-grouping">
            {t('products.form.deliveryGrouping')}
          </Label>
          <Controller
            name="deliveryGroupingRule"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled={isMutating}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={t('products.form.deliveryGrouping')}
                  />
                </SelectTrigger>
                <SelectContent>
                  {DELIVERY_GROUPING_RULES.map((rule) => (
                    <SelectItem key={rule} value={rule}>
                      <div className="flex flex-col">
                        <span>
                          {t(DELIVERY_GROUPING_CONFIG[rule].labelKey)}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        {/* Description (English) */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="description-en">
            {t('products.form.descriptionEn')}
          </Label>
          <Textarea
            id="description-en"
            {...register('description.en')}
            placeholder={t('products.form.descriptionEn')}
            disabled={isMutating}
            rows={2}
          />
        </div>

        {/* Description (Arabic) */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="description-ar">
            {t('products.form.descriptionAr')}
          </Label>
          <Textarea
            id="description-ar"
            {...register('description.ar')}
            placeholder={t('products.form.descriptionAr')}
            disabled={isMutating}
            rows={2}
            dir="rtl"
          />
        </div>

        {/* Images */}
        <div className="flex flex-col gap-3">
          <Label htmlFor="variant-images">
            {t('products.form.images', 'Images')}
          </Label>

          {pickImageFiles ? (
            <Button
              type="button"
              variant="outline"
              onClick={handlePickImageFiles}
              disabled={isMutating}
            >
              <Upload className="h-4 w-4 me-2" />
              {t('products.form.addImages', 'Add images')}
            </Button>
          ) : (
            <Input
              id="variant-images"
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageFilesChange}
              disabled={isMutating}
            />
          )}

          <p className="text-xs text-muted-foreground">
            {t(
              'products.form.variantImagesHint',
              'Upload one or more variant images.',
            )}
          </p>

          {(existingImages.length > 0 || newImageFiles.length > 0) && (
            <div className="grid grid-cols-1 gap-3 rounded-md border p-3 sm:grid-cols-2">
              {existingImages.map((image, index) => (
                <div
                  key={`${image.url}-${index}`}
                  className="rounded-md border bg-background p-2"
                >
                  <div className="h-28 overflow-hidden rounded-md border bg-muted/30">
                    <Avatar className="h-full w-full rounded-none">
                      <AvatarImage
                        src={
                          image.url.startsWith('http://') ||
                          image.url.startsWith('https://')
                            ? image.url
                            : `${apiUrl}${image.url}`
                        }
                        alt={getImageNameFromUrl(image.url)}
                        className="h-full w-full object-cover"
                      />
                      <AvatarFallback className="rounded-none bg-muted/30 text-muted-foreground">
                        <ImageIcon className="h-6 w-6" />
                      </AvatarFallback>
                    </Avatar>
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <Badge variant="secondary" className="max-w-[70%] truncate">
                      {getImageNameFromUrl(image.url)}
                    </Badge>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-destructive hover:text-destructive"
                      onClick={() => handleRemoveExistingImage(index)}
                      disabled={isMutating}
                    >
                      <Trash2 className="h-3.5 w-3.5 me-1" />
                      {t('common.remove', 'Remove')}
                    </Button>
                  </div>
                </div>
              ))}

              {newImagePreviews.map((preview, index) => (
                <div
                  key={`${preview.name}-${index}`}
                  className="rounded-md border bg-background p-2"
                >
                  <div className="h-28 overflow-hidden rounded-md border bg-muted/30">
                    <Avatar className="h-full w-full rounded-none">
                      <AvatarImage
                        src={preview.url}
                        alt={preview.name}
                        className="h-full w-full object-cover"
                      />
                      <AvatarFallback className="rounded-none bg-muted/30 text-muted-foreground">
                        <ImageIcon className="h-6 w-6" />
                      </AvatarFallback>
                    </Avatar>
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <Badge variant="secondary" className="max-w-[70%] truncate">
                      {preview.name}
                    </Badge>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-destructive hover:text-destructive"
                      onClick={() => handleRemoveNewFile(index)}
                      disabled={isMutating}
                    >
                      <Trash2 className="h-3.5 w-3.5 me-1" />
                      {t('common.remove', 'Remove')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {imageError && (
            <p className="text-sm text-destructive">{imageError}</p>
          )}
        </div>

        {/* SAP Material Code */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="sap-material-code">
            {t('products.form.sapMaterialCode')}
          </Label>
          <Input
            id="sap-material-code"
            {...register('sapMaterialCode')}
            placeholder={t('products.form.sapMaterialCodePlaceholder')}
            disabled={isMutating}
            className="font-mono"
          />
        </div>

        {/* SAP Storage Location Code */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="sap-storage-location-code">
            {t('products.form.sapStorageLocationCode')}
          </Label>
          <Input
            id="sap-storage-location-code"
            {...register('sapStorageLocationCode')}
            placeholder={t('products.form.sapStorageLocationCodePlaceholder')}
            disabled={isMutating}
            className="font-mono"
          />
        </div>

        {/* Active Status */}
        <div className="flex items-center justify-between py-2 border rounded-md px-3">
          <Label htmlFor="is-active" className="cursor-pointer">
            {t('products.form.isActive')}
          </Label>
          <Switch
            id="is-active"
            checked={isActive}
            onCheckedChange={(checked) => setValue('isActive', checked)}
            disabled={isMutating}
          />
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isMutating}
          >
            <X className="h-4 w-4 me-2" />
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={isMutating}>
            {isUploadingImages ? (
              <Upload className={cn('h-4 w-4 me-2', 'animate-spin')} />
            ) : (
              <Save
                className={cn('h-4 w-4 me-2', isSaving && 'animate-spin')}
              />
            )}
            {isUploadingImages
              ? t('products.form.uploadingImages', 'Uploading images...')
              : isSaving
                ? t('common.saving')
                : t('common.save')}
          </Button>
        </div>
      </form>
    </BaseDrawer>
  );
}
